// src/components/common/LazyImage.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Skeleton } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

interface LazyImageProps {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  aspectRatio?: string;
  thumbnail?: string;
  blurDataURL?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  borderRadius?: number | string;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  rootMargin?: string;
  threshold?: number;
  placeholderColor?: string;
  showErrorIcon?: boolean;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  width = '100%',
  height = 'auto',
  aspectRatio,
  thumbnail,
  blurDataURL,
  objectFit = 'cover',
  borderRadius = 0,
  onClick,
  className,
  style,
  rootMargin = '200px',
  threshold = 0.1,
  placeholderColor = '#f1f5f9',
  showErrorIcon = true,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(!!thumbnail);
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Setup Intersection Observer
  useEffect(() => {
    if (!imgRef.current) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Disconnect once in view
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rootMargin, threshold]);

  // Handle image load
  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setShowThumbnail(false);
  }, []);

  // Handle image error
  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true);
  }, []);

  // Handle thumbnail load
  const handleThumbnailLoad = useCallback(() => {
    // Thumbnail is loaded, now we can start loading the full image
  }, []);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width,
    height: aspectRatio ? 'auto' : height,
    aspectRatio: aspectRatio || undefined,
    overflow: 'hidden',
    borderRadius,
    backgroundColor: placeholderColor,
    cursor: onClick ? 'pointer' : 'default',
    ...style,
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit,
    transition: 'opacity 0.3s ease-in-out, filter 0.3s ease-in-out',
  };

  return (
    <Box
      ref={imgRef}
      className={className}
      sx={containerStyle}
      onClick={onClick}
    >
      {/* Skeleton placeholder */}
      {!isLoaded && !hasError && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius,
          }}
        />
      )}

      {/* Blur placeholder (if provided) */}
      {blurDataURL && !isLoaded && !hasError && (
        <Box
          component="img"
          src={blurDataURL}
          alt=""
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit,
            filter: 'blur(20px)',
            transform: 'scale(1.1)',
          }}
        />
      )}

      {/* Thumbnail (low quality placeholder) */}
      {thumbnail && showThumbnail && isInView && (
        <Box
          component="img"
          src={thumbnail}
          alt=""
          onLoad={handleThumbnailLoad}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit,
            filter: 'blur(10px)',
            transform: 'scale(1.05)',
            opacity: isLoaded ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}

      {/* Main image */}
      {isInView && !hasError && (
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          sx={{
            ...imageStyle,
            opacity: isLoaded ? 1 : 0,
          }}
        />
      )}

      {/* Error state */}
      {hasError && showErrorIcon && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8fafc',
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
        </Box>
      )}
    </Box>
  );
};

export default LazyImage;
